import { marked } from 'marked';

import * as errorCodes from '../shared/ai-error-defs.js';

import { getConfig, resolveUrl } from '../shared/utils.js';
import { preflight } from '../shared/ai-preflight.js';
import { BUCKET, BUCKET_MESSAGE, toAiError } from '../shared/ai-errors.js';
import { createTypewriter } from '../shared/ai-typewriter.js';
import { gAudio } from '../widgets/controls.js';

const currentProgram = getConfig('currentProgram') || 1;
const hasAiAccess = !getConfig('aiLevel')?.startsWith('none') ;
const aiDepth = getConfig('aiDepth');
const base = '/ai-api/capsule-state';

const program = aiDepth.programs[currentProgram];

const NAV_PANEL_WIDTH = 300;
const NAV_CONTENT_WIDTH = 780;
const NAV_CARD_WIDTH = NAV_CONTENT_WIDTH * 0.87;
const navFitsInGutter = () => window.innerWidth >= NAV_CARD_WIDTH + NAV_PANEL_WIDTH * 2;
const defaultPrefs = { 'showMenu': navFitsInGutter(), 'episodePrefs': {} };
const menuPrefs = JSON.parse(localStorage.getItem('menuPrefs')) || defaultPrefs;

const AI_LABEL_ID = 'aiLabel';

const storyStates = {};
const pendingLoads = new Set();

const inFlight = new Set();

const depthTextTemplate = (l1, l2) => `Rewrite the previous response at response-depth level ${l1}.
         After that, treat ${l2} as the active response-depth level until changed again.`;

const DEPTH_INDICATORS = [
    '■□□',
    '■■□',
    '■■■',
    //'■■■■'
];

/** Lock to prevent multiple calls to the same key */
const withLock = async (key, fn) => {
    if (inFlight.has(key)) return;
    inFlight.add(key);
    try {
        return await fn();
    } finally {
        inFlight.delete(key);
    }
};

const postHeaders = {
    'Platform': 'testing',
    'Flavor': 'testing',
    'Build': 'testing',
    'Content-Type': 'application/json',
    'Language': getConfig('language'),
    'Authorization': `Bearer ${getConfig('token')}`,
};

const CHEVRON_SVG = `
    <svg viewBox="0 0 24 24" width="36" height="36">
        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/>
    </svg>
`;

/**
 * Observes the intersection of the netflix strip with the page,
 * when the netflix strip is not in view the navigation tab
 * is moved closer to the top of the page.
 * @type {IntersectionObserver}
 */
export const intObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            const nav = document.getElementById('content-nav');
            const navTab = document.getElementById('nav-tab');
            if (nav && navTab) {
                if (entry.isIntersecting) {
                    nav.style.height = '55%';
                    nav.style.top = '350px';
                    navTab.style.top = '350px';
                } else {
                    nav.style.height = '90%';
                    nav.style.top = '60px';
                    navTab.style.top = '60px';
                }
            }
        });
    },
);

const openNavMenu = () => {
    const nav = document.getElementById('content-nav');
    const navTab = document.getElementById('nav-tab');
    if (!nav || !navTab) return;
    nav.style.width = `${NAV_PANEL_WIDTH}px`;
    navTab.style.left = `-${NAV_PANEL_WIDTH}px`;
    menuPrefs.showMenu = true;
    localStorage.setItem('menuPrefs', JSON.stringify(menuPrefs));
};

const closeNavMenu = () => {
    const nav = document.getElementById('content-nav');
    const navTab = document.getElementById('nav-tab');
    if (!nav || !navTab) return;
    nav.style.width = '0px';
    navTab.style.left = '0px';
    menuPrefs.showMenu = false;
    localStorage.setItem('menuPrefs', JSON.stringify(menuPrefs));
};

export const resetNavMenu = () => {
    const menuContainer = document.getElementById('content-nav');
    const navTab = document.getElementById('nav-tab');
    if (!menuContainer || !navTab) return;
    menuContainer.style.width = '0px';
    navTab.style.left = `-${NAV_PANEL_WIDTH}px`;
    const navMenu = document.getElementById('story-sections');
    navMenu?.replaceChildren();
};

const setNavVisibility = (visible) => {
    const nav = document.getElementById('content-nav');
    const navTab = document.getElementById('nav-tab');
    if (!nav || !navTab) return;
    nav.style.display = visible ? '' : 'none';
    navTab.style.display = visible ? '' : 'none';
};

const isDeepDiveActive = () => {
    const pane = storyContainer.active?.querySelector('[data-ai-pane="deep-dive"]');
    return !!pane && pane.style.display !== 'none';
};

/** Shows the navigation menu only while the Deep Dive pane is active */
const updateNavMenu = () => {
    const visible = isDeepDiveActive();
    setNavVisibility(visible);
    if (!visible) return;
    if (menuPrefs.showMenu && navFitsInGutter()) {
        openNavMenu();
    } else {
        closeNavMenu();
    }
};

function saveNavMenuPrefs(nav, sectionId) {
    if (!sectionId) return; // Skip if no ID is defined
    const episodeNum = document.querySelector('[data-episode]').dataset.episode;
    if (!menuPrefs.episodePrefs[episodeNum]) {
        menuPrefs.episodePrefs[episodeNum] = { items: {} };
    }
    menuPrefs.episodePrefs[episodeNum].items[sectionId] = { expanded: nav.open };

    localStorage.setItem('menuPrefs', JSON.stringify(menuPrefs));
}

const addNavSection = (capsule) => {
    const menuAnchors = document.getElementById('story-sections');
    const section = getNavigationItem(capsule);

    if (capsule.isDiveIn) {
        const lastItem = menuAnchors.lastElementChild; //<li>
        const details = lastItem.querySelector('details');
        details.classList.remove('no-sub-items');
        let subSection = lastItem.querySelector('ul.dive-in');
        if (!subSection) {
            subSection = document.createElement('ul');
            subSection.className = 'dive-in';
            details.appendChild(subSection);
        }
        subSection.appendChild(section);
        const episodeNum = document.querySelector('[data-episode]').dataset.episode;
        details.open = menuPrefs.episodePrefs[episodeNum]?.items[capsule.key]?.expanded ?? true;

        details.addEventListener('toggle', (event) => {
            saveNavMenuPrefs(event.target, capsule.key);
        });
    } else {
        menuAnchors.appendChild(section);
    }
};

/**
 * Creates navigation item for the menu
 * Recursive function, exit condition = No children
 * @param capsule The capsule being added
 * @return {HTMLLIElement} A new list element
 */
const getNavigationItem = (capsule) => {
    const section = document.createElement('li');
    const detailsTag = document.createElement('details');

    // Logic for when changing back to an existing depth
    let index = document.querySelectorAll(`[data-anchor-ref="${capsule.key}"]`).length;

    detailsTag.innerHTML =
        `<summary>
            <a href="#"
                data-action="go-to"
                data-anchor-ref="${capsule.key}"
                data-index-ref="${index}"
            >
            ${marked.parseInline(capsule.numberedTitle)}
            <span class="nav-indicator">${DEPTH_INDICATORS[capsule.depth]}</span></a>
        </summary>`;
    detailsTag.classList.add('no-sub-items');
    detailsTag.id = `nav-${capsule.key}.${index}`;

    section.appendChild(detailsTag);

    return section;
};

const renderCapsule = (capsule, isLatest) => {
    const storyRef = storyContainer.current;
    const domNode = document.querySelector(`[data-tab-ai="${storyRef}"]`);
    if (domNode !== null) {
        const capsuleHTML = getCapsuleHTML(capsule, isLatest);

        domNode.insertAdjacentHTML('beforeend', capsuleHTML);
    }
};

function getCapsuleHTML(capsule, isLatest = false) {
    let titlePrefix = capsule.key.split('.')[0];
    let parentCapsule = 0;
    if (!capsule.isDiveIn) {
        titlePrefix = `Language Capsule ${titlePrefix.split('.')[0]}`;
    } else { //DIVE IN
        parentCapsule = titlePrefix;
        const capsules  = storyStates[storyContainer.current].state.capsules;

        //Search unique titles within the diveIns
        const diveIns = new Set();
        capsules.forEach((item) => {
            if (item.key.startsWith(parentCapsule.toString()) && item.isDiveIn) {
                diveIns.add(item.title);
            }
        });
        const uniqueTitles = [...diveIns];
        titlePrefix = `${parentCapsule}.${uniqueTitles.indexOf(capsule.title) + 1}`;
    }
    capsule.numberedTitle = `${titlePrefix} - ${capsule.title}`;

    let depthText = aiDepth.names[capsule.depth];
    if (isLatest && getConfig('login') && hasAiAccess) {
        depthText = linkifyDepth(depthText);
    }

    const titleHTML = `
            ${marked.parseInline(capsule.numberedTitle)}
            <span class="depth-reference">
                ${DEPTH_INDICATORS[capsule.depth]}&nbsp;
                (${depthText})
            </span>
        `;

    let sectionTitle = `<h2>${titleHTML}</h2>`;

    if (capsule.isDiveIn) {
        sectionTitle = `<h3>${titleHTML}</h3>`;
    }

    let index = storyContainer.active.querySelectorAll(`[data-section="${capsule.key}"]`).length;

    return `
            <div data-story="${storyContainer.current}"
            class="ai-capsule"
            data-section="${capsule.key}"
            data-index="${index}"
            data-parent="${parentCapsule}"
            >
                ${sectionTitle}
                ${marked(capsule.body)}
            </div>
        `;
}

const linkifyDepth = (depthText) => {
    const depthNameContent = document.createElement('button');
    depthNameContent.textContent = depthText;
    depthNameContent.classList.add('ai-link-button');
    depthNameContent.classList.add('depth-popup-trigger');
    return depthNameContent.outerHTML;
};

const unlinkDepthTriggers = (storyRef) => {
    const container = document.querySelector(`[data-tab-ai="${storyRef}"]`);
    container?.querySelectorAll('.ai-capsule .depth-popup-trigger').forEach((button) => {
        button.replaceWith(button.textContent);
    });
};

const setActivePane = (pane) => {
    const container = storyContainer.active;
    if (!container) return;

    container.classList.toggle('deep-dive-active', pane === 'deep-dive');
    container.querySelectorAll('[data-ai-pane]').forEach((el) => {
        el.style.display = el.dataset.aiPane === pane ? '' : 'none';
    });

    syncPaneToggle();
    updateNavMenu();

    window.dispatchEvent(new Event('scroll')); // fixes sticky shift bug
};

const switchPane = (button) => {
    if (button.classList.contains('active')) return;
    if (gAudio && !gAudio.paused) gAudio.pause();
    setActivePane(button.dataset.aiPaneTarget);
};

export const showStoryPane = () => setActivePane('story');

const setPaneToggleVisibility = (visible) => {
    const toggle = document.querySelector('.deep-dive-toggle');
    if (!toggle) return;
    toggle.style.display = visible ? '' : 'none';
    if (!visible) {
        document.querySelector('.controls')?.classList.remove('deep-dive-active');
    }
};

const syncPaneToggle = () => {
    const toggle = document.querySelector('.deep-dive-toggle');
    if (!toggle) return;
    const pane = isDeepDiveActive() ? 'deep-dive' : 'story';
    toggle.querySelectorAll('[data-ai-pane-target]').forEach((b) => {
        b.classList.toggle('active', b.dataset.aiPaneTarget === pane);
    });
    document.querySelector('.controls')?.classList.toggle('deep-dive-active', pane === 'deep-dive');
    toggle.style.display = '';
};

const showOptions = (storyRef) => {
    const options = document.querySelector(`[data-tab-ai-options="${storyRef}"]`);
    if (options) { options.style.display = 'block'; }
};

const showMiscLinks = (storyRef) => {
    const depthLink = document.querySelector(`[data-depth-story="${storyRef}"]`);
    if (depthLink) { depthLink.style.display = 'block'; }
    const exploreLink = document.querySelector(`[data-explore-story="${storyRef}"]`);
    if (exploreLink) { exploreLink.style.display = 'block'; }

    enableAISelections(storyRef);
};

const prepareOptions = (options, storyRef) => {

    const divOptions = document.querySelector(`[data-tab-ai-options="${storyRef}"]`);
    divOptions?.remove();

    const div = document.createElement('div');
    div.dataset.tabAiOptions = storyRef;
    div.className = 'ai-options-container';
    div.style.display = 'none';

    for (const option of options) {
        const buttonText = marked.parse(option.text);
        const buttonHTML = `
            <button class="capsule-button" data-option="${option.level}">
                <div class="icon-circle">${CHEVRON_SVG}</div>
                <div class="ai-button-content">${buttonText}</div>
            </button>
        `;
        div.insertAdjacentHTML('beforeend', buttonHTML);
    }

    div.addEventListener('click', async (event) => {
        const button = event.target.closest('.capsule-button');
        if (!button) return;

        await withLock(`respond:${storyRef}`, async () => {
            disableAISelections(storyRef);

            if (!await runPreflight()) {
                enableAISelections(storyRef);
                return;
            }

            await chooseOption(getContentKey(storyRef), button.dataset.option, button.textContent.trim());
        });
    });

    //Append options to AI div
    const aiDiv = document.querySelector(`[data-tab-ai="${storyRef}"]`);
    aiDiv?.appendChild(div);
};

async function updateState() {
    storyStates[storyContainer.current].state = await call(
        'GET',
        `${base}?contentKey=${encodeURIComponent(getContentKey(storyContainer.current))}`,
    );
}

/**
 * Function called when changing the depth level for the AI conversation
 * @param contentKey {string} - The key for the story content
 * @param depthLevel {number} - The level to be changed to
 * @param save {boolean} - Flag to keep the selected level or keep current
 * @return {Promise<void>}
 */
async function setDepth(contentKey, depthLevel, save = false) {
    addActivityIndicator(storyContainer.current);
    const res = await call('POST', base, { contentKey, depthLevel, save });
    if (!res?.capsule) {
        await presentAiError(res);
        enableAISelections(storyContainer.current);
        removeActivityIndicator();
        return;
    }
    await updateState();
    let newDepth = getCurrentDepthLevel();
    if (save) {
        newDepth = depthLevel;
    }
    const optionText = depthTextTemplate(aiDepth.names[depthLevel], aiDepth.names[newDepth]);

    handleIncomingCapsule(res.capsule, optionText);
    addNavSection(res.capsule);
    updateNavMenu();
}

/**
 * Function called when selecting an option for AI conversation
 * @param contentKey - The key for the story content
 * @param optionKey - The key for the selected option
 * @param optionText - The text of the selected option
 * @return {Promise<void>}
 */
async function chooseOption(contentKey, optionKey, optionText) {

    addActivityIndicator(storyContainer.current);
    const res = await call('POST', base, { contentKey, option: optionKey });
    if (!res?.capsule) {
        await presentAiError(res);
        enableAISelections(storyContainer.current);
        removeActivityIndicator();
        return;
    }
    await updateState();
    handleIncomingCapsule(res.capsule, optionText);
    addNavSection(res.capsule);
    updateNavMenu();
}


/**
 * Uses the capsule to add the new capsule to screen with a typed effect.
 * @param capsule {Object} - The capsule object received in the response
 * @param optionText {string} - The text of the selected action
 */
function handleIncomingCapsule(capsule, optionText) {

    const capsuleContainer = document.querySelector(`[data-tab-ai="${storyContainer.current}"]`);
    const sectionId = `capsule-${capsule.key}`;
    const repeatCapsules = document.querySelectorAll(`[data-section="${sectionId}"]`).length;
    const div = document.createElement('div');
    div.dataset.section = sectionId;
    div.dataset.index = `${repeatCapsules}`;

    const optionsDiv = document.querySelector(`[data-tab-ai-options="${storyContainer.current}"]`);
    if (optionsDiv) { optionsDiv.remove(); }
    removeMiscLinks(storyContainer.current);
    unlinkDepthTriggers(storyContainer.current);

    removeActivityIndicator();
    appendSelected(capsuleContainer, optionText);
    capsuleContainer.appendChild(div);
    addActivityIndicator(storyContainer.current);

    appendDepthLink(storyContainer.current);
    prepareOptions(capsule.options, storyContainer.current);
    appendExploreAgainLink(storyContainer.current);

    let typewriter = createTypewriter(
        (text) => {
            const container = storyContainer.active
                                .querySelector(`[data-section="${sectionId}"][data-index="${repeatCapsules}"]`);
            container.innerHTML = text;
        },
        () => {
            removeActivityIndicator();
            showOptions(storyContainer.current);
            showMiscLinks(storyContainer.current);
            appendAILabel(storyContainer.current);
        },
    );

    typewriter.push(getCapsuleHTML(capsule, true));

    typewriter.close();

}

/**
 * Disables or Enables buttons while waiting for an action
 * @param storyRef {string} - The current story
 * @param disabled {boolean} - The flag for enabling or disabling
 */
const setAISelectionsDisabled = (storyRef, disabled) => {
    const optionsDiv = document.querySelector(`[data-tab-ai-options="${storyRef}"]`);
    optionsDiv?.querySelectorAll('button').forEach((button) => {
        button.disabled = disabled;
    });
    const aiContainer = document.querySelector(`[data-tab-ai="${storyRef}"]`);
    aiContainer?.querySelectorAll('.ai-link-button').forEach((button) => {
        button.disabled = disabled;
    });
};

const disableAISelections = (storyRef) => setAISelectionsDisabled(storyRef, true);
const enableAISelections = (storyRef) => setAISelectionsDisabled(storyRef, false);

/**
 * Parses Markdown text into HTML and appends it as a styled option to a parent element.
 * @param {HTMLElement} parent The parent element
 * @param {string} selected The text of the selected option
 */
const appendSelected = (parent, selected) => {
    const markUp = marked.parse(selected, { async: false });
    const selection = document.createElement('div');
    selection.innerHTML = markUp;
    selection.className = 'selected-option';
    parent?.appendChild(selection);
    selection.scrollIntoView({ behavior: 'smooth' });
};

const appendDepthLink = (storyRef) => {

    //Remove the link if it exists in the document
    removeMiscLinks(storyRef);

    if (!getConfig('login') || !hasAiAccess) return;

    const depthLink = createDepthPopupLink('Change Response Style', storyRef);
    depthLink.style.display = 'none';
    depthLink.dataset.depthStory = storyRef;

    const domNode = document.querySelector(`[data-tab-ai="${storyRef}"]`);
    domNode.appendChild(depthLink);
};

const createDepthPopupLink = (linkText) => {
    const depthLink = document.createElement('button');
    depthLink.textContent = linkText;
    depthLink.classList.add('ai-link-button');
    depthLink.classList.add('outer-link');
    depthLink.classList.add('depth-popup-trigger');
    return depthLink;
};

const appendExploreAgainLink = (storyRef) => {
    const existingLink = document.querySelector(`[data-explore-story="${storyRef}"]`);
    if (existingLink) { existingLink.remove(); }

    if (!getConfig('login') || !hasAiAccess) return;

    const exploreLink = document.createElement('button');
    exploreLink.style.display = 'none';
    exploreLink.textContent = 'End / Restart';
    exploreLink.classList.add('ai-link-button');
    exploreLink.classList.add('outer-link');
    exploreLink.dataset.exploreStory = storyRef;

    exploreLink.addEventListener('click', async () => {
        await withLock(`explore:${storyRef}`, () => exploreAgain(storyRef));
    });

    const domNode = document.querySelector(`[data-tab-ai="${storyRef}"]`);
    domNode.appendChild(exploreLink);
};

function appendAILabel(storyRef) {
    const target = (
        location.protocol + '//' + location.host +
        (getConfig('root') === '/latino' ? '/latino' : '') +
        '/ai-use-policy'
    );

    const aiLabel = document.createElement('p');
    const link = `<a target='_blank' href='${target}'>About our use of AI</a>`;
    aiLabel.innerHTML = `Created with AI · ${link}`;
    aiLabel.className = 'ai-label';
    aiLabel.id = AI_LABEL_ID;

    const domNode = document.querySelector(`[data-tab-ai="${storyRef}"]`);
    domNode.appendChild(aiLabel);
}

function removeAILabel() {
    const existing = document.getElementById(AI_LABEL_ID);
    if (existing) { existing.remove(); }
}

const exploreAgain = async (story) => {
    const contentKey = getContentKey(story);
    let restart = true;
    try {
        await call('POST', `${base}/reset`, { contentKey });
    } catch (error) {
        if (error.status !== 409) {
            restart = false;
            await presentAiError(error);
        }
    } finally {
        if (restart) {
            await restartConversation();
        }
    }
};

/**
 *
 * @param e {PointerEvent}
 * @return {Promise<void>}
 */
const depthPopup = async (e) => {
    e.preventDefault();
    const keepTxt = 'Keep this level for the rest of the Deep Dive';
    const dialog = document.getElementById('ai-generic');
    const depthForm = document.getElementById('ai-dialog-form');
    const currentDepthLevel = getCurrentDepthLevel();

    //Validate form before sending to server
    depthForm.addEventListener('submit', (event) => {
        event.preventDefault();
        if (event.submitter.value === 'cancel') {
            dialog.close();
        } else {
            dialog.close(event.submitter.value);
        }
    }, { once: true });

    const labels = [
        'depth-quick',
        'depth-essential',
        'depth-nuanced',
        //'depth-deep-dive'
    ];
    const descriptions = [
        'For concise, to-the-point answers',
        'For balanced and thoughtful explanations',
        'For extra details and analysis',
        //'For a thorough exploration of the topic',
    ];
    const depthOptions = aiDepth.names.slice(0, -1).map((name, index) => ({
        value: index,
        name: name,
        label: labels[index],
        indicator: DEPTH_INDICATORS[index],
        description: descriptions[index],
    }));

    const optionsHTML = depthOptions.map((option) => `
        <div class="depth-card-option">
            <input type="radio" id="${option.label}"
            name="depth-level"
            value="${option.value}"
            ${currentDepthLevel === option.value ? 'disabled' : ''}>
            <label for="${option.label}" class="depth-selection-card">
                <span class="depth-indicator">${option.indicator}</span>
                <span class="depth-details">
                    <span class="depth-level">${option.name}</span>
                    <span class="depth-level-description">${option.description}</span>
                </span>
            </label>
        </div>
    `).join('');

    const depthLevelForm = `
            <div class="depth-group">
                <p style="font-weight: bold;font-size: 1.2em;">Change Response Style</p>
                ${optionsHTML}
                <label id="keep-depth-label">
                    <input type="checkbox" name="keep-depth" id="keep-depth"/> ${keepTxt}
                </label>
            </div>
        `;

    const depthButtons = [
        { label: 'Cancel', value: 'cancel' },
        { label: 'Submit', value: 'submit', click: async () => {
                const formData = new FormData(depthForm);
                const depthInput = formData.get('depth-level');
                const keepDepth = formData.get('keep-depth');

                if (!depthInput || typeof depthInput !== 'string') return;

                const depthNum = parseInt(depthInput, 10);

                if (Number.isNaN(depthNum) || currentDepthLevel === depthNum) return;

                // Only call to AI if user is logged in and has an active subscription
                // Can reach here if user logged out but page is not refreshed
                if (getConfig('login') && hasAiAccess) {
                    const story = storyContainer.current;
                    await withLock(
                        `respond:${story}`,
                        () => setDepth(getContentKey(story), depthNum, keepDepth !== null),
                    );
                }
            },
        },
    ];

    await showDialog(depthLevelForm, depthButtons, null, (dialog) => {
        const submitBtn = dialog.querySelector('#ai-footer-form button[value="submit"]');
        if (!submitBtn) return;

        // No level selected yet
        submitBtn.disabled = true;
        dialog.querySelectorAll('input[name="depth-level"]').forEach((radio) => {
            radio.addEventListener('change', () => { submitBtn.disabled = false; });
        });
    });
};

const removeMiscLinks = (storyRef) => {
    const existingDepthLink = document.querySelector(`[data-depth-story="${storyRef}"]`);
    if (existingDepthLink) { existingDepthLink.remove(); }
    const existingLink = document.querySelector(`[data-explore-story="${storyRef}"]`);
    if (existingLink) { existingLink.remove(); }
};

const getContentKey = (story) => {
    const elem = document.querySelector(`[data-tab="${story}"][data-content-key]`);
    return elem?.dataset.contentKey ?? null;
};

/**
 * @typedef {Object} ButtonConfig
 * @property {string} label - The Button text.
 * @property {string} value - The value returned by the button when clicked
 * @property {string} [method] - Optional {FormMethod} for the button, [post, get, dialog], default is dialog.
 * @property {string} [action] - Optional {FormAction}, Depends on method being post or get.
 * @property {Function} [click] - Optional Function to execute, defaults to closing. Buttons must resolve the promise;
 */

/**
 *
 * @param {string} message
 * @param {ButtonConfig[]} buttons - An array of button configuration objects to show in the popup.
 * @param {string | null} [headerText] - Optional. The header for the popup
 * @param {Function} [onReady] - Optional. Runs with the dialog element after it is built, before it opens.
 * @param isLeftAligned - flag to left align message
 * @return {Promise} - The user's selected option
 */
const showDialog = (message, buttons = [], headerText = null, onReady = null, isLeftAligned = false) => {
    return new Promise((resolve) => {
        const aiDialog = document.getElementById('ai-generic');
        if (isLeftAligned) aiDialog.classList.add('left-aligned');
        const dialogCallbacks = {};
        const header = document.getElementById('ai-dialog-title');
        if (headerText) {
            header.textContent = headerText;
            header.parentElement.style.display = 'block';
        }

        const body = document.getElementById('ai-dialog-message');
        body.innerHTML = message;
        body.scrollTop = 0;

        const footer = document.getElementById('ai-footer-form');

        //Resetting dialog dependencies
        footer.replaceChildren();

        buttons.forEach((btnConfig) => {
            const btn = document.createElement('button');
            btn.classList.add('ai-button');
            btn.textContent = btnConfig.label;
            btn.value = btnConfig.value;
            btnConfig.method && (btn.formMethod = btnConfig.method);
            btnConfig.action && (btn.formAction = btnConfig.action);
            btnConfig.action && (btn.formTarget = '_blank');
            btnConfig.action && (btn.addEventListener('click', () => {
                aiDialog.close();
            }));

            if (btnConfig.click) {
               dialogCallbacks[btn.value] = btnConfig.click;
            }

            footer.appendChild(btn);
        });

        aiDialog?.addEventListener('close', async () => {
            const result = aiDialog.returnValue;
            const actionToRun = dialogCallbacks[result];

            if (actionToRun) {
                await actionToRun();
            }

            //Cleanup
            aiDialog.returnValue = '';
            resolve(result);

        }, { once: true });

        if (onReady) { onReady(aiDialog); }

        aiDialog.showModal();
    });
};

const addActivityIndicator = (storyRef) => {
    const indicator = document.querySelector('#activity-indicator');
    if (!indicator) { // allow only one indicator div to be inserted
        const div = document.createElement('div');
        div.id = 'activity-indicator';
        div.style.margin = '35px';
        div.className = 'dot-pulse';
        const aiDiv = document.querySelector(`[data-tab-ai="${storyRef}"]`);
        aiDiv?.appendChild(div);
    }
    removeAILabel();
};

const removeActivityIndicator = () => {
    const indicator = document.getElementById('activity-indicator');
    if (indicator) { indicator.remove(); }
};

/**
 * Event listener for opening and closing the navigation menu
 */
window.addEventListener('load', () => {
    const navTab = document.getElementById('nav-tab');
    const navContent = document.getElementById('content-nav');
    if (navTab && navContent) {
        navTab.addEventListener('click', openNavMenu);
        document.getElementById('min-button').addEventListener('click', closeNavMenu);
    }
});

/**
 * Mimics anchor functionality without the need for the id attribute.
 * Uses attribute data-section
 * Example HTML : <div data-section="1.0.1" data-index="3">
 * @param section The section in the document
 * @param index Index for repeat capsules
 */
function jumpToSection(section, index) {
    const selector = `[data-section="${CSS.escape(section)}"][data-index="${index}"]`;
    storyContainer.active.querySelector(selector)?.scrollIntoView();
}

/**
 * Event listener for click events
 */
document.addEventListener('click', async function (e) {
    if (e.target.dataset.action === 'go-to') {
        e.preventDefault();
        jumpToSection(e.target.dataset.anchorRef, parseInt(e.target.dataset.indexRef));
    } else if (e.target.matches('.depth-popup-trigger')) {
        if (await runPreflight()) {
            await depthPopup(e);
        }
    } else if (e.target.matches('[data-ai-pane-target]')) {
        switchPane(e.target);
    }
});

/**
 * Render the AI content for the article being viewed
 * @param storyRef The story being referenced
 */
export async function renderAIContent(storyRef) {

    if (!storyRef) {
        storyRef = storyContainer.current;
    }
    resetNavMenu();
    const domNode = document.querySelector(`[data-tab-ai="${storyRef}"]`);
    if (!domNode) {
        setNavVisibility(false);
        setPaneToggleVisibility(false);
        return;
    }
    syncPaneToggle();
    updateNavMenu();

    // Observing when the news strip is shown to move the floating menu tab
    const strip = document.getElementsByClassName('netflix-loading-content');
    if (strip[0]) {
        intObserver.observe(strip[0]);
    }

    storyStates[storyRef] = {};
    try {
        const state = await call('GET', `${base}?contentKey=${encodeURIComponent(getContentKey(storyRef))}`);

        storyStates[storyRef].state = state;
        if (state && state.capsules) {
            domNode.replaceChildren();
            let broken = false;
            state.capsules.forEach((capsule, index) => {
                if (index > 0) {
                    let userSelection =
                        inferSelection(capsule, state.capsules[index - 1], state.capsules[index + 1] || null);
                    if (userSelection) {
                        const aiDiv = document.querySelector(`[data-tab-ai="${storyRef}"]`);
                        appendSelected(aiDiv, userSelection);
                    } else {
                        broken = true;
                    }
                }

                renderCapsule(capsule, !broken && index === state.capsules.length - 1);
                addNavSection(capsule);

                if (index === state.capsules.length - 1) {
                    if (!broken) {
                        appendDepthLink(storyRef);
                        prepareOptions(capsule.options, storyRef);
                        showOptions(storyRef);
                    }
                    if (index > 0) appendExploreAgainLink(storyRef);
                    showMiscLinks(storyRef);
                    appendAILabel(storyRef);
                }

            });

            updateNavMenu();
            if (broken) presentAiError(BUCKET.SERVER_ERROR);
        }
    } catch (error) {
        const aiError = await toAiError(error);
        if (aiError.bucket === BUCKET.MAINTENANCE) {
            renderLangServerMaintenance(storyRef);
        } else {
            await presentAiError(aiError);
        }
    } finally {
        pendingLoads.delete(storyRef);
        removeActivityIndicator();
    }
}

function getSection(key) {
    return key.split('.', 2).join('.');
}
function inferSelection(capsule, previousCapsule, nextCapsule) {

    let userSelection;
    if (getSection(capsule.key) === getSection(previousCapsule.key)) {
        let nextDepth = getCurrentDepthLevel();
        if (nextCapsule) {
            nextDepth = nextCapsule.depth;
        }
        userSelection = depthTextTemplate(
            aiDepth.names[capsule.depth],
            aiDepth.names[nextDepth],
        );
    } else { //top-level capsule
        userSelection = previousCapsule?.options?.find((option) => option.level === capsule.key)?.text;
    }
    return userSelection;
}

const renderLangServerMaintenance = (storyRef) => {
    const capsuleContainer = document.querySelector(`[data-tab-ai="${storyRef}"]`);
    if (capsuleContainer) {
        capsuleContainer.replaceChildren();
        const maintenanceDiv = document.createElement('div');
        maintenanceDiv.style.textAlign = 'center';
        maintenanceDiv.innerHTML = `
            <div id="lang-server-maintenance">
                <img alt="maintenance" srcset="../../maintenance/gears_3.gif"/>
            </div>
            <div>${errorCodes.MAINTENANCE_ERROR}</div>
        `;
        const button = document.createElement('button');
        button.innerHTML = 'Refresh';
        button.addEventListener('click', () => window.location.reload());
        button.classList.add('button', 'primary');
        button.style.marginTop = '10px';
        maintenanceDiv.appendChild(button);
        capsuleContainer.appendChild(maintenanceDiv);
    }
};

const restartConversation = async () => {
    const domNode = document.querySelector(`[data-tab-ai="${storyContainer.current}"]`);
    domNode?.replaceChildren();
    await renderAIContent(storyContainer.current);
    storyStates[storyContainer.current].conversing = false;
    storyStates[storyContainer.current].depth = program?.default ?? 1;
    showOptions(storyContainer.current);
    showMiscLinks(storyContainer.current);
};

/**
 * The container for the active tab
 */
const storyContainer = {
    get active() {
        const currentTab = document.querySelector('.dynamic-tab.lang-data-container:not([style*="display: none"])');
        const singleStory = document.querySelector('.lang-data-container');
        return currentTab ?? singleStory ?? null;
    },

    get current() {
        return this.active?.dataset?.tab;
    },

};

const confirmCondition = async (text, accept) => {
    let acceptError = null;
    const selection = await showDialog(text, [
        { label: 'Cancel', value: 'cancel' },
        { label: 'Agree', value: 'agree', click: async () => {
            try {
                await accept();
            } catch (e) {
                acceptError = e;
            }
        } },
    ], null, null, true);

    if (selection === 'cancel') return false;
    if (acceptError) {
        await presentAiError(acceptError);
        return false;
    }
    return true;
};

const runPreflight = async () => {
    let check;
    try {
        check = await preflight.check(postHeaders, false);
    } catch (error) {
        await presentAiError(error);
        return false;
    }

    if (!check.ok) {
        if (check.reason === errorCodes.ANONYMOUS || check.reason === errorCodes.NO_SUBSCRIPTION) {
            await showDialog(errorCodes.NO_SUBSCRIPTION_MSG, subscribeButtons);
            return false;
        }

        if (check.reason === errorCodes.AUDIO_ONLY) {
            await showDialog(errorCodes.AUDIO_ONLY_MSG, upgradeButtons);
            return false;
        }

        if (check.reason === errorCodes.CONDITIONS_PENDING) {
            for (const name of check.data.pending) {
                const text = await preflight.getConditionText(name, postHeaders);
                const accepted = await confirmCondition(text, async () => {
                    await preflight.acceptCondition(name, postHeaders);
                });

                if (!accepted) return false;
            }
        }

        if (check.reason === errorCodes.NO_LANG_TOKENS) {
            const text = await preflight.getConditionText('terms', postHeaders);
            const accepted = await confirmCondition(text, () => preflight.initLangTokens());

            if (!accepted) return false;
        }

    }

    return true;
};

const getCurrentDepthLevel = () =>
    storyStates[storyContainer.current]?.state.activeDepth ??
    program?.default ??
    1;
/*************************************************************************/
/* ******************  POPUP BUTTON CONFIGS **************************** */
/*************************************************************************/

/** @type {ButtonConfig[]} */
const renewButtons = [
    { label: 'Cancel', value: '' },
    {
        label: 'Renew',
        value: 'renew',
        method: 'get',
        action: resolveUrl('/member'),
    },
];

const upgradeButtons = [
    { label: 'Not Now', value: '' },
    {
        label: 'Upgrade',
        value: 'upgrade',
        method: 'get',
        action: resolveUrl('/member'),
    },
];

/** @type {ButtonConfig[]} */
const balanceButtons = [
    { label: 'Cancel', value: 'cancel' },
    {
        label: 'Add More Tokens',
        value: 'buy-tokens',
        method: 'get',
        action: resolveUrl('/purchase-tokens'),
    },
];

/** @type {ButtonConfig[]} */
const subscribeButtons = [
    { label: 'Not now', value: 'cancel' },
    {
        label: 'Learn More',
        value: 'subscribe',
        method: 'get',
        action: resolveUrl('/subscription-rates'),
    },
];

const okButton = [{ label: 'Ok', value: 'cancel', click: () => enableAISelections(storyContainer.current) }];

/** @type {ButtonConfig[]} */
const reloadButtons = [
    { label: 'Ok', value: 'reload', click: () => window.location.reload() },
];

/** @type {ButtonConfig[]} */
const conversationGoneButtons = [
    { label: 'Cancel', value: 'cancel', click: () => enableAISelections(storyContainer.current) },
    { label: 'Restart Deep Dive', value: 'restart', click: () => restartConversation() },
];

const subscriptionButtons = () => (getConfig('login') ? renewButtons : subscribeButtons);

/** Error bucket -> buttons */
const BUCKET_BUTTONS = {
    [BUCKET.UNAUTHORIZED]: reloadButtons,
    [BUCKET.NOT_SUBSCRIBED]: subscriptionButtons,
    [BUCKET.AUDIO_ONLY]: upgradeButtons,
    [BUCKET.NO_TOKENS]: balanceButtons,
    [BUCKET.CONVERSATION_GONE]: conversationGoneButtons,
    [BUCKET.MAINTENANCE]: reloadButtons,
    [BUCKET.STREAM_FAILED]: reloadButtons,
    [BUCKET.SERVER_ERROR]: okButton,
    [BUCKET.OFFLINE]: okButton,
};

/** Single entry point for error handling */
const presentAiError = async (source, headerText = null) => {
    const aiError = await toAiError(source);
    const bucket = aiError.bucket || BUCKET.SERVER_ERROR;

    const messageEntry = BUCKET_MESSAGE[bucket] ?? BUCKET_MESSAGE[BUCKET.SERVER_ERROR];
    const message = typeof messageEntry === 'function' ? messageEntry() : messageEntry;

    /** @type {Function|Array} */
    let buttons = BUCKET_BUTTONS[bucket] ?? okButton;
    if (typeof buttons === 'function') {
        buttons = buttons();
    }

    console.error('[AI error]', bucket, aiError.status ?? '', aiError.code ?? '', aiError.rawMessage ?? '');
    return showDialog(message, buttons, headerText);
};

/**
 * @typedef {Object} CapsuleOption
 * @property {string} level - Capsule key.
 * @property {string} text - Label title.
 */

/**
 * @typedef {Object} Capsule
 * @property {string} key - Unique identifier for the current capsule.
 * @property {boolean} isDiveIn - Flag if dive-in.
 * @property {string} title - The capsule title.
 * @property {number} depth - The depth level for the current capsule.
 * @property {string} body - The Markdown-formatted content text.
 * @property {CapsuleOption[]} options - Navigation choices available for this capsule.
 */

/**
 * @typedef {Object} CapsulePayload
 * @property {Capsule} capsule - The content and metadata container object.
 * @property {number} activeDepth - The current viewing depth level in the UI.
 */

/**
 * @typedef {Object} CurrentState
 * @property {Capsule[]} capsules - An array containing multiple capsules.
 * @property {number} activeDepth - The current viewing depth level in the UI.
 */

/**
 * Function to call the database
 *
 * @param {'GET' | 'POST'} method - The HTTPRequest method
 * @param {string} path - The url to be called
 * @param {string | Object} [body] - The data to be sent
 * @return {Promise<CapsulePayload|CurrentState>}
 */

async function call(method, path, body) {
    const res = await fetch(path, {
        method,
        credentials: 'same-origin',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
    });

    const text = await res.text();

    let json;

    try {
        json = JSON.parse(text);
    } catch {
        json = text;
    }

    return json;
}
